/* eslint-disable no-irregular-whitespace */
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';

import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { useTranslation } from '@/config/i18n';
import useSearch from '@/hooks/useSearch';
import {
  parseQueryValue,
  randomUUID,
  toastErrorMessage,
  toastMessage,
} from '@/lib/utils';
import { apiRequest } from '@/services';
import { DATE_FORMAT, HTTP_STATUS } from '@/shared/constants';
import { ACTION_TYPE } from '@/shared/constants/user';
import {
  userManagementSearchFormSchema,
  UserManagementSearchFormType,
} from '@/shared/schema/user-management';
import { yupResolver } from '@hookform/resolvers/yup';
import UserManagementModalConfirm from './UserManagementModalConfirm';
import DialogCustom from '@/components/common/ui/dialog';
import { ObjectLiteral } from '@/types/object';

type Props = {
  selectedRows: { id: number }[];
  setSelectedRows: (selectedRows: any) => void;
  setChangeStatusUser: React.Dispatch<React.SetStateAction<string>>;
  onSearch: () => void;
};

const UserManagementSearchForm: React.FC<Props> = ({
  selectedRows,
  setSelectedRows,
  setChangeStatusUser,
  onSearch,
}: Props) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { toast } = useContext(ToastContext);
  const [loading, setLoading] = useState<boolean>();
  const [dialogConfirmApproveVisible, setDialogConfirmApproveVisible] =
    useState(false);
  const [dialogConfirmRejectVisible, setDialogConfirmRejectVisible] =
    useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [errors, setErrors] = useState<ObjectLiteral[]>();

  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const formMethods = useForm<UserManagementSearchFormType>({
    mode: 'onChange',
    resolver: yupResolver(userManagementSearchFormSchema),
    shouldFocusError: false,
    defaultValues: {
      keyword: '',
      start_date: null,
      end_date: null,
    },
  });
  const keywordMsg = t('user_management_list.search_placeholder');
  const { handleSearch, queryRaw } = useSearch();
  const { handleSubmit, getValues, setValue, reset, watch } = formMethods;
  const startDate = watch('start_date');
  const endDate = watch('end_date');

  async function onSubmit() {
    setLoading(true);
    let start_date = getValues('start_date');
    let end_date = getValues('end_date');

    start_date = start_date
      ? dayjs(start_date.toString()).format('YYYY-MM-DD')
      : ('' as unknown as any);
    end_date = end_date
      ? dayjs(end_date.toString()).format('YYYY-MM-DD')
      : ('' as unknown as any);

    handleSearch({ ...getValues(), start_date, end_date });
    setLoading(false);
    onSearch();
  }

  function handleReset() {
    reset();
    onSubmit();
  }

  function handleError(error: any) {
    const { status_code } = error;

    if (status_code === HTTP_STATUS.FORM_ERROR) {
      const msg = t('user_management_list.dialog.error.user_not_found');
      toast?.current?.show(toastErrorMessage(msg));
    }
  }

  async function handleChangeStatusUser(actionType: string) {
    if (!selectedRows.length) return;

    setLoadingGlobal(true);
    const { data, error } = await apiService.usersCoachRequest.create({
      ids: selectedRows.map((row) => row.id),
      action: actionType,
    });

    if (error) {
      handleError(error);
      setLoadingGlobal(false);
      setDialogConfirmApproveVisible(false);
      setDialogConfirmRejectVisible(false);
      return;
    }

    if (data.data.length > 0) {
      setErrors(data.data);
      setDialogVisible(true);
      setDialogConfirmApproveVisible(false);
      setDialogConfirmRejectVisible(false);
      setLoadingGlobal(false);
      setChangeStatusUser(randomUUID());
      return;
    }

    setErrors([]);
    const messageSuccess =
      actionType === ACTION_TYPE.APPROVE
        ? t('user_management.approve_success')
        : t('user_management.reject_success');
    toast?.current?.show(toastMessage(messageSuccess));

    setChangeStatusUser(randomUUID());
    setDialogConfirmApproveVisible(false);
    setDialogConfirmRejectVisible(false);
    setSelectedRows([]);
    setLoadingGlobal(false);
  }

  function handleShowDialogConfirmApprove() {
    const nextDialogVisibleValue = !dialogConfirmApproveVisible;
    setDialogConfirmApproveVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;
  }

  function handleShowDialogConfirmReject() {
    const nextDialogVisibleValue = !dialogConfirmRejectVisible;
    setDialogConfirmRejectVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;
  }

  function handleReject() {
    handleShowDialogConfirmReject();
  }

  function handleApprove() {
    handleShowDialogConfirmApprove();
  }

  const getAllFieldNames = () => {
    const values = getValues();
    return Object.keys(values);
  };

  function mapValueFromQuery() {
    getAllFieldNames().forEach((field: any) => {
      const val = queryRaw[field];
      if (val) {
        setValue(field, parseQueryValue(field, val));
      }
    });
  }

  useEffect(() => {
    mapValueFromQuery();
  }, []);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          className="mt-[16px] flex gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <ButtonCustom
            type="button"
            severity="secondary"
            onClick={() => handleApprove()}
            className="min-w-[72px] !border-success-300 !bg-success-50 !text-success-700 font-semibold"
            disabled={selectedRows.length === 0}
          >
            {t('user_management_list.approve_button')}
          </ButtonCustom>
          <ButtonCustom
            type="button"
            severity="secondary"
            onClick={() => handleReject()}
            className="min-w-[72px] !border-error-200 !bg-error-50 !text-error-700 font-semibold"
            disabled={selectedRows.length === 0}
          >
            {t('user_management_list.reject_button')}
          </ButtonCustom>
          <div className="w-[450px] search-input relative">
            <span className="pi pi-search absolute top-[14px] left-[14px] text-gray-500 text-[15px] z-40" />
            <InputTextCustomForm
              name="keyword"
              className="!pl-[40px]"
              placeholder={keywordMsg}
            />
          </div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="start_date"
            dateFormat={DATE_FORMAT}
            wrapClass="min-w-[140px]"
            showIcon
            maxDate={endDate ? new Date(endDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <div className="flex-full-center">~</div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="end_date"
            dateFormat={DATE_FORMAT}
            wrapClass="min-w-[140px]"
            showIcon
            minDate={startDate ? new Date(startDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <ButtonCustom
            type="button"
            severity="secondary"
            onClick={() => handleReset()}
            className="min-w-[60px]"
          >
            <span className="pi pi-refresh" />
          </ButtonCustom>
          <ButtonCustom
            className="min-w-[60px]"
            type="submit"
            disabled={loading}
          >
            <span className="pi pi-search" />
          </ButtonCustom>
        </form>
      </FormProvider>

      <UserManagementModalConfirm
        dialogConfirmEditVisible={dialogConfirmApproveVisible}
        setDialogConfirmEditVisible={setDialogConfirmApproveVisible}
        handleChangeStatusUser={handleChangeStatusUser}
        loadingGlobal={loadingGlobal}
        actionType={ACTION_TYPE.APPROVE}
        title={t('user_management.approve_confirm.title')}
        content={t('user_management.approve_confirm.content')}
      />

      <UserManagementModalConfirm
        dialogConfirmEditVisible={dialogConfirmRejectVisible}
        setDialogConfirmEditVisible={setDialogConfirmRejectVisible}
        handleChangeStatusUser={handleChangeStatusUser}
        loadingGlobal={loadingGlobal}
        actionType={ACTION_TYPE.REJECT}
        title={t('user_management.reject_confirm.title')}
        content={t('user_management.reject_confirm.content')}
      />

      <DialogCustom
        className="w-[600px]"
        header={t('user_management_list.dialog.error.title')}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        blockScroll
      >
        <div className="max-h-[300px] overflow-y-scroll">
          <p>この度、コーチのアカウント承認を行いました。 </p>
          <p>結果は、10名のうち </p>
          <p>
            <span className='text-error-600'>{selectedRows.length - (errors?.length || 0)}</span>
            名のコーチの方々が承認され、アカウントを発行されました。
          </p>
          <p>
            <span className='text-error-600'>
              {errors?.length || 0}
            </span>
            名のコーチの方々は承認できませんでした。以下に詳細をお知らせいたします。
          </p>
          {errors?.map((error: any, index) => {
            return (
              <div key={index} className="mb-2 mt-2 font-bold">
                {error.data.last_name_kanji}
                {error.data.first_name_kanji}　{error.data.email}
              </div>
            );
          })}
          上記、再度ご確認いただき、お試しください。
        </div>
      </DialogCustom>
    </>
  );
};

export default UserManagementSearchForm;
